import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { Check, Copy, Pencil } from 'lucide-react';
import { AnswerView } from './AnswerView';
import { Markdown } from './Markdown';
import { ComposingSlot, RecordReadout, WorkingLine } from './ComposingReadout';
import { tokens } from './tokens';
import { cardEntrance, cardSlowReveal, COUNT_LEAD_MS } from './motion';
import { TIMING } from './animationConfig';
import { QueryReveal } from './QueryReveal';
import { describeQuery } from './queryNarration';
import type { ChatMessage } from './copilotTypes';

// Visually-hidden (screen-reader-only) style for live-region announcements.
const SR_ONLY: React.CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

async function copyText(text: string): Promise<boolean> {
  if (!text.trim()) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy clipboard path for embedded/insecure contexts.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    return copied;
  } catch {
    return false;
  }
}

function copyableResponseText(message: ChatMessage): string {
  if (message.role === 'user') return message.text;
  const answer = message.answer;
  const parts = [
    message.text,
    answer && 'narrative' in answer ? answer.narrative : '',
    answer?.kind === 'chart' ? answer.summary ?? '' : '',
    answer?.kind === 'analysis' ? answer.packet.findings.map((finding) => finding.statement).join('\n') : '',
    answer?.kind === 'analysis' ? answer.packet.facts.flatMap((fact) => fact.values.map((value) => `${value.label}: ${value.value}${value.unit ? ` ${value.unit}` : ''}`)).join('\n') : '',
  ].filter(Boolean);
  return parts.filter((part, index) => parts.indexOf(part) === index).join('\n\n').trim();
}

function ActionButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Button
      size="small"
      variant="text"
      startIcon={icon}
      onClick={onClick}
      sx={{
        minWidth: 0,
        px: 0.75,
        py: 0.25,
        color: tokens.text.secondary,
        fontFamily: tokens.fontFamily,
        fontSize: 12,
        textTransform: 'none',
        '&:hover': { color: tokens.text.primary, bgcolor: tokens.surface.page },
      }}
    >
      {label}
    </Button>
  );
}

/**
 * A short, best-effort provenance label for a completed turn: "cached" for a scripted cache beat,
 * else a compact model name derived from the raw backend model id (Opus / Sonnet / Haiku) — falling
 * back to the raw id so an unrecognized model still reads honestly. Returns null when nothing to show.
 */
function sourceBadgeLabel(meta?: { source?: 'cache' | 'live'; model?: string }): string | null {
  if (!meta) return null;
  if (meta.source === 'cache') return 'cached';
  const id = meta.model;
  if (!id) return null;
  const lower = id.toLowerCase();
  if (lower.includes('opus')) return 'Opus';
  if (lower.includes('sonnet')) return 'Sonnet';
  if (lower.includes('haiku')) return 'Haiku';
  return id;
}

/**
 * The chart hand-off — the thought-process step. Every chart answer leads with a plain-language query
 * description for a beat, then the chart mounts and builds part by part on a settled card.
 *
 * Counted turns fill the lead with the signature readout: the count reads up ("Querying N records…"),
 * locks, then DEMOTES to "N records in range" as the chart joins (COUNT_LEAD_MS). No-count turns use a
 * shorter lead (queryLeadMs) — just long enough to read the query. Reduced motion skips the lead.
 */
function ChartAnswerSlot({ message }: { message: ChatMessage }) {
  const reduceMotion = useReducedMotion() ?? false;
  const hasCount = typeof message.count === 'number';
  // The lead beat before the chart mounts: the count read-up fills it when present, else a short beat so
  // the formed query is still read first.
  const lead = hasCount ? COUNT_LEAD_MS : TIMING.queryLeadMs;
  const [mounting, setMounting] = React.useState(!reduceMotion);
  React.useEffect(() => {
    if (reduceMotion) return;
    const t = setTimeout(() => setMounting(false), lead);
    return () => clearTimeout(t);
  }, [reduceMotion, lead]);

  // Plain-language description of the query, derived from the chosen chart spec (never model prose).
  const spec = (message.answer as any)?.spec;
  const queryText = spec ? describeQuery(spec) : '';

  return (
    // Keep the 330px floor through the whole hand-off so the lead→chart swap never collapses or snaps
    // (the chart settles ≈this height; taller content still grows past it).
    <Box sx={{ position: 'relative', minHeight: 330 }}>
      {/* A plain-language query description remains visible while the chart is being prepared. */}
      <QueryReveal queryText={queryText} lead={mounting} />
      {hasCount ? (
        <RecordReadout count={message.count as number} mounting={mounting} />
      ) : (
        // No-count lead: the same shimmer carries from "Composing…" to "Querying…" so the query reveal
        // reads as part of the working phase, then the chart joins.
        mounting && <WorkingLine label="Querying the database…" />
      )}
      {/* Step: the empty CARD slowly materializes (cardSlowReveal), HOLDS, then the data draws part by
          part inside it (the ECharts frame beat > the card reveal, so the parts clearly follow the card). */}
      {!mounting && (
        <motion.div variants={cardSlowReveal} initial={reduceMotion ? 'show' : 'hidden'} animate="show">
          <AnswerView answer={message.answer!} />
        </motion.div>
      )}
    </Box>
  );
}

/**
 * One transcript turn. User turns render their question; assistant turns render the streamed
 * (markdown) preamble plus, once data lands, the dispatched answer body — both in the SAME bubble so
 * the prose and its chart read as a single reply. Owns layout + the source-badge placeholder.
 */
export function MessageBubble({
  message,
  onStop,
  onRetry,
  onFollowup,
  onEdit,
}: {
  message: ChatMessage;
  /** Abort the in-flight request for a streaming assistant turn. */
  onStop?: () => void;
  /** Re-run the question that produced an errored/cancelled assistant turn. */
  onRetry?: () => void;
  /** Re-ask a follow-up chip shown under a completed turn. */
  onFollowup?: (q: string) => void;
  /** Load a user's prompt back into the composer for editing. */
  onEdit?: (text: string) => void;
}) {
  // Hooks must run on every render in the same order — call this BEFORE the user-turn early return.
  // Reduced motion: the answer entrance skips straight to the settled state, no rise/fade.
  const reduceMotion = useReducedMotion();
  const [copied, setCopied] = React.useState(false);
  const textToCopy = copyableResponseText(message);

  const handleCopy = async () => {
    if (await copyText(textToCopy)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  };

  if (message.role === 'user') {
    // User turns are right-aligned cardinal bubbles (the "you" side); assistant turns flow full-width on
    // the left so charts get the room. alignSelf overrides the transcript Stack's stretch.
    return (
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '78%' }}>
        <Box
          sx={{
            bgcolor: tokens.brand.cardinal,
            color: '#fff',
            px: 2,
            py: 1.1,
            borderRadius: '16px',
            borderBottomRightRadius: '4px',
            boxShadow: '0 1px 2px rgba(140,21,21,0.25)',
          }}
        >
          <Typography
            sx={{ fontFamily: tokens.fontFamily, color: '#fff', fontSize: 15, fontWeight: 500, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}
          >
            {message.text}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.25 }}>
          <ActionButton
            label={copied ? 'Copied' : 'Copy'}
            icon={copied ? <Check size={14} /> : <Copy size={14} />}
            onClick={handleCopy}
          />
          {onEdit && (
            <ActionButton label="Edit" icon={<Pencil size={14} />} onClick={() => onEdit(message.text)} />
          )}
        </Box>
      </Box>
    );
  }

  const isError = message.status === 'error';
  const isStreaming = message.status === 'streaming';
  const streamingNoAnswer = isStreaming && !message.answer;
  // A turn the user can re-run: it errored, or was stopped mid-flight without producing an answer.
  const canRetry = (isError || (message.status === 'cancelled' && !message.answer)) && !!onRetry;
  const followups = message.status === 'done' ? message.suggestions ?? [] : [];
  // Best-effort provenance badge (Sonnet / Opus / cached), shown once the turn completes.
  const badge = message.status === 'done' ? sourceBadgeLabel(message.meta) : null;
  // Prose/clarify render their (validated) narrative inside AnswerView, so the bubble must not also
  // render message.text for them — that would double the prose.
  const answerOwnsText = message.answer?.kind === 'prose' || message.answer?.kind === 'clarify' || message.answer?.kind === 'analysis' || message.answer?.kind === 'email_sent';
  // Every chart answer flows through ChartAnswerSlot: it leads with the query panel, holds a beat (the
  // count read-up fills it when present), then mounts the chart for its part-by-part build. Non-chart
  // answers (prose/clarify/fallback/escape) build with the normal card entrance.
  const isChart = message.answer?.kind === 'chart';
  // The turn owns the single answer entrance (ChartCard no longer wraps its own — that was a double
  // entrance on chart cards).
  // Concise screen-reader announcement for the turn's state — the streamed reply is otherwise silent.
  // One short message per state (not per token) so the polite live region doesn't flood.
  const liveStatus = isStreaming
    ? 'Composing a response'
    : message.status === 'done'
      ? 'Response ready'
      : message.status === 'error'
        ? `Error: ${message.text}`
        : message.status === 'cancelled'
          ? 'Request cancelled'
          : '';

  return (
    <Box>
      {/* Visually-hidden polite live region: announces working/ready/error to assistive tech. */}
      <span style={SR_ONLY} role="status" aria-live="polite">{liveStatus}</span>
      {message.text && !answerOwnsText && (
        isError ? (
          <Typography
            sx={{ fontFamily: tokens.fontFamily, color: tokens.semantic.danger, fontSize: 15, lineHeight: 1.6, mb: message.answer ? 2 : 0 }}
          >
            {message.text}
          </Typography>
        ) : (
          <Box sx={{ mb: message.answer ? 2 : 0 }}>
            {/* While the turn streams, words fade in as they arrive instead of snapping to full ink. */}
            <Markdown text={message.text} stream={isStreaming} />
          </Box>
        )
      )}

      {/* A small inline shimmer + timer that trails the streamed reasoning. The label reflects the REAL
          stage: before any reasoning streams we're reading the question; once it's flowing the model is
          reasoning over the data (that prose above is its actual chain of thought). */}
      {streamingNoAnswer && (
        <ComposingSlot label={message.text ? 'Reasoning over the cohort’s data…' : 'Reading your question…'} />
      )}

      {message.progress && (
        <Typography sx={{ fontFamily: tokens.fontFamily, color: tokens.text.secondary, fontSize: 12, mt: 0.5 }}>
          {message.progress.label} · {message.progress.completed}/{message.progress.total}
        </Typography>
      )}

      {isStreaming && onStop && (
        <Button
          onClick={onStop}
          size="small"
          variant="text"
          sx={{ mt: 0.5, textTransform: 'none', fontFamily: tokens.fontFamily, color: tokens.text.secondary, minWidth: 0, px: 1 }}
        >
          Stop
        </Button>
      )}

      {message.status === 'cancelled' && !message.answer && (
        <Typography sx={{ fontFamily: tokens.fontFamily, color: tokens.text.secondary, fontSize: 14 }}>
          Cancelled.
        </Typography>
      )}

      {message.answer && (
        isChart ? (
          // Query panel → hold → part-by-part chart build (the slot owns the chart's entrance).
          <ChartAnswerSlot message={message} />
        ) : (
          <motion.div variants={cardEntrance} initial={reduceMotion ? 'show' : 'hidden'} animate="show">
            <AnswerView answer={message.answer} onClarify={onFollowup} />
          </motion.div>
        )
      )}

      {badge && (
        <Typography
          data-testid="source-badge"
          sx={{
            fontFamily: tokens.fontFamily,
            color: tokens.text.secondary,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
            mt: 1,
          }}
        >
          {badge}
        </Typography>
      )}

      {canRetry && (
        <Button
          onClick={onRetry}
          size="small"
          variant="text"
          sx={{ mt: 0.5, textTransform: 'none', fontFamily: tokens.fontFamily, color: tokens.brand.cardinal, minWidth: 0, px: 1 }}
        >
          Retry
        </Button>
      )}

      {followups.length > 0 && onFollowup && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
          {followups.map((q) => (
            <Chip
              key={q}
              label={q}
              onClick={() => onFollowup(q)}
              variant="outlined"
              size="small"
              sx={{ fontFamily: tokens.fontFamily, borderColor: tokens.surface.border, color: tokens.text.secondary }}
            />
          ))}
        </Stack>
      )}

      {textToCopy && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.25 }}>
          <ActionButton
            label={copied ? 'Copied' : 'Copy'}
            icon={copied ? <Check size={14} /> : <Copy size={14} />}
            onClick={handleCopy}
          />
        </Box>
      )}
    </Box>
  );
}
